import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { PublisherDetail } from 'types/publisher';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import './PublisherCardMolecule.scss';

export interface PublisherCardMoleculeProps {
  publisher: PublisherDetail;
  onClick?: (slug: string) => void;
  className?: string;
}

const PublisherCardMolecule: React.FC<PublisherCardMoleculeProps> = ({
  publisher,
  onClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);

  const cardStyle = {
    '--publisher-card-hue': `${publisher.hue}`,
  } as React.CSSProperties;

  const classes = [
    'marketplace-publisher-card',
    onClick ? 'marketplace-publisher-card--clickable' : '',
    hover && onClick ? 'marketplace-publisher-card--hover' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => onClick?.(publisher.slug);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(publisher.slug);
    }
  };

  return (
    <article
      className={classes}
      style={cardStyle}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      onMouseEnter={onClick ? () => setHover(true) : undefined}
      onMouseLeave={onClick ? () => setHover(false) : undefined}
      aria-label={
        onClick
          ? t('marketplace.publishers.card.ariaLabel', { name: publisher.display_name })
          : undefined
      }
    >
      {/* 56px hue-gradient banner strip */}
      <div className="marketplace-publisher-card__banner" aria-hidden="true" />

      {/* Identity row: glyph + name stack */}
      <div className="marketplace-publisher-card__header">
        <div className="marketplace-publisher-card__glyph">
          {publisher.logoUrl ? (
            <img
              src={publisher.logoUrl}
              alt={t('marketplace.publishers.card.logoAlt', { name: publisher.display_name })}
              className="marketplace-publisher-card__logo-img"
            />
          ) : (
            <ScopeCoinAtom
              scope="center"
              glyph={publisher.glyph}
              channelName={publisher.display_name}
              glyphKind={publisher.glyph_kind}
              hue={publisher.hue}
              size="md"
            />
          )}
        </div>
        <div className="marketplace-publisher-card__identity">
          <span className="marketplace-publisher-card__name">
            <span className="marketplace-publisher-card__name-text">
              {publisher.display_name}
            </span>
            {publisher.is_official && (
              <StatusBadgeAtom
                tone="success"
                icon="pi pi-verified"
                label={t('marketplace.publishers.officialBadge')}
                size="medium"
                className="marketplace-publisher-card__official"
              />
            )}
          </span>
          <span className="marketplace-publisher-card__slug">/{publisher.slug}</span>
        </div>
      </div>

      {/* Bio — 3-line clamp */}
      <p className="marketplace-publisher-card__bio">{publisher.bio}</p>

      {/* Stats footer: channels / subscribers / followers */}
      <div className="marketplace-publisher-card__stats">
        <span className="marketplace-publisher-card__stat">
          <i className="pi pi-package" aria-hidden="true" />
          {t('marketplace.publishers.card.channelCount', { count: publisher.channelCount })}
        </span>
        <span className="marketplace-publisher-card__stat">
          <i className="pi pi-users" aria-hidden="true" />
          {(publisher.subscriberCount ?? 0).toLocaleString()}
        </span>
        <span className="marketplace-publisher-card__stat">
          <i className="pi pi-layers" aria-hidden="true" />
          {(publisher.followerCount ?? 0).toLocaleString()}
        </span>
      </div>
    </article>
  );
};

PublisherCardMolecule.displayName = 'PublisherCardMolecule';

export default PublisherCardMolecule;
